import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import WorkerApplication from './pages/WorkerApplication';
import ApplicationSuccess from './pages/ApplicationSuccess';
import CustomerDashboard from './pages/CustomerDashboard';
import BookingPage from './pages/BookingPage';
import PaymentPage from './pages/PaymentPage';
import PaymentSuccess from './pages/PaymentSuccess';
import WorkerDashboardV2 from './pages/WorkerDashboardV2';
import WorkerProfile from './pages/WorkerProfile';
import WorkerOnboarding from './pages/WorkerOnboarding';
import OwnerDashboard from './pages/OwnerDashboard';
import OfferLetter from './pages/OfferLetter';
import { ForgotPassword, ResetPassword } from './pages/ForgotPassword';
import ChangePassword from './pages/ChangePassword';
import DemoCompletionForm from './pages/DemoCompletionForm';
import ReviewsPage from './pages/ReviewsPage';
import VerifyIDCard from './pages/VerifyIDCard';
import CareersPage from './pages/CareersPage';
import BookingConfirmation from './pages/BookingConfirmation';
import NotificationPermissionPrompt from './components/NotificationPermissionPrompt';
import InstallAppPrompt from './components/InstallAppPrompt';
import ServicesPage from './pages/ServicesPage';
import DriverApplicationPage from './pages/DriverApplicationPage';
import RideBookingPage from './pages/RideBookingPageV2';
import RideConfirmation from './pages/RideConfirmation';
import DriverRidesPage from './pages/DriverRidesPage';
import TrackOrder from './pages/TrackOrder';
import GuestCheckout from './pages/GuestCheckout';
import BookingSuccess from './pages/BookingSuccess';
import BookingCancelled from './pages/BookingCancelled';
import CourierBookingPage from './pages/CourierBookingPage';
import ErrandBookingPage from './pages/ErrandBookingPage';
import LaundryBookingPage from './pages/LaundryBookingPage';
import WorkerOnboardingWizard from './pages/WorkerOnboardingWizard';
import WorkerMobileApp from './pages/WorkerMobileApp';
import BookingTracker from './pages/BookingTracker';
import LeaveReview from './pages/LeaveReview';

import './App.css';

// Protected Route Component - checks for password change requirement
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated, mustChangePassword, logout, clearPasswordChangeRequired } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="animate-pulse-soft text-[var(--primary)] text-lg">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Save the intended URL to redirect after login
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Force password change if required
  if (mustChangePassword) {
    return (
      <ChangePassword 
        user={user} 
        onPasswordChanged={clearPasswordChangeRequired}
        onLogout={logout}
      />
    );
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    if (user?.role === 'owner') return <Navigate to="/owner/dashboard" replace />;
    if (user?.role === 'worker') return <Navigate to="/worker/dashboard" replace />;
    return <Navigate to="/customer/dashboard" replace />;
  }

  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="animate-pulse-soft text-[var(--primary)] text-lg">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    if (user?.role === 'owner') return <Navigate to="/owner/dashboard" replace />;
    if (user?.role === 'worker') return <Navigate to="/worker/dashboard" replace />;
    return <Navigate to="/customer/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/contact" element={<Navigate to="/#contact" replace />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      <Route path="/apply" element={<WorkerApplication />} />
      <Route path="/application-success" element={<ApplicationSuccess />} />
      <Route path="/offer/:offerId" element={<OfferLetter />} />
      <Route path="/demo/completion-form" element={<DemoCompletionForm />} />
      <Route path="/verify/:cardId" element={<VerifyIDCard />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/drive" element={<DriverApplicationPage />} />
      {/* Public Services Page - Guest can browse and book */}
      <Route path="/services" element={<ServicesPage />} />
      
      {/* Ride Confirmation - after booking a ride (public, no auth required) */}
      <Route path="/ride-confirmation/:rideId" element={<RideConfirmation />} />
      <Route path="/ride-confirmation" element={<RideConfirmation />} />
      
      {/* Track Order - public page for guests to track rides/bookings */}
      <Route path="/track" element={<BookingTracker />} />
      <Route path="/track/:reference" element={<BookingTracker />} />
      
      {/* Leave Review - public page for customers to review completed bookings */}
      <Route path="/review/:bookingId" element={<LeaveReview />} />
      
      {/* Guest Checkout - Payment-First Booking Flow */}
      <Route path="/guest-checkout" element={<GuestCheckout />} />
      <Route path="/booking/success" element={<BookingSuccess />} />
      <Route path="/booking/cancelled" element={<BookingCancelled />} />

      {/* Customer Routes */}
      <Route 
        path="/customer/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerDashboard />
          </ProtectedRoute>
        } 
      />
      {/* Booking page - accessible for both logged-in users and guests */}
      <Route 
        path="/customer/book/:serviceId" 
        element={<BookingPage />} 
      />
      <Route 
        path="/book/:serviceId" 
        element={<BookingPage />} 
      />
      {/* Ride Booking - dedicated page for rides */}
      <Route path="/book-ride" element={<RideBookingPage />} />
      <Route path="/customer/book-ride" element={<RideBookingPage />} />
      
      {/* Courier Booking - dedicated page for package delivery */}
      <Route path="/book-courier" element={<CourierBookingPage />} />
      <Route path="/book-delivery" element={<CourierBookingPage />} />
      
      {/* Errand Booking - dedicated page for errands */}
      <Route path="/book-errand" element={<ErrandBookingPage />} />
      <Route path="/book-errands" element={<ErrandBookingPage />} />
      
      {/* Laundry Booking - dedicated page for laundry */}
      <Route path="/book-laundry" element={<LaundryBookingPage />} />
      
      {/* Worker Onboarding Wizard - Lyft-inspired 8-step flow */}
      <Route path="/join" element={<WorkerOnboardingWizard />} />
      <Route path="/worker-onboarding" element={<WorkerOnboardingWizard />} />
      
      <Route 
        path="/customer/pay/:bookingId" 
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <PaymentPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/payment-success" 
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <PaymentSuccess />
          </ProtectedRoute>
        } 
      />
      {/* Guest booking confirmation - no auth required */}
      <Route path="/booking-confirmation/:bookingId" element={<BookingConfirmation />} />

      {/* Worker Routes */}
      <Route 
        path="/worker/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['worker']}>
            <WorkerDashboardV2 />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/worker/mobile" 
        element={
          <ProtectedRoute allowedRoles={['worker']}>
            <WorkerMobileApp />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/worker/profile" 
        element={
          <ProtectedRoute allowedRoles={['worker']}>
            <WorkerProfile />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/worker/onboarding" 
        element={
          <ProtectedRoute allowedRoles={['worker']}>
            <WorkerOnboarding />
          </ProtectedRoute>
        } 
      />
      {/* Driver Rides Page - dedicated mobile-friendly page for drivers */}
      <Route 
        path="/driver/rides" 
        element={
          <ProtectedRoute allowedRoles={['worker']}>
            <DriverRidesPage />
          </ProtectedRoute>
        } 
      />

      {/* Owner Routes */}
      <Route 
        path="/owner/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['owner']}>
            <OwnerDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Preview Routes - Load without auth check since they use previewMode */}
      <Route 
        path="/customer-preview" 
        element={<CustomerDashboard previewMode={true} />} 
      />
      <Route 
        path="/worker-preview" 
        element={<WorkerDashboardV2 previewMode={true} defaultTab="jobs" />} 
      />
      <Route 
        path="/driver-preview" 
        element={<WorkerDashboardV2 previewMode={true} defaultTab="rides" />} 
      />

      {/* Public Routes */}
      <Route path="/reviews" element={<ReviewsPage />} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster 
          position="top-center" 
          richColors 
          offset="60px"
          closeButton
          duration={3000}
          gap={8}
          toastOptions={{
            style: {
              fontFamily: 'Manrope, sans-serif',
              marginTop: window.self !== window.top ? '60px' : '0px',
            },
            className: 'toast-mobile-offset'
          }}
        />
        {/* Global Notification Permission Prompt */}
        <NotificationPermissionPrompt />
        {/* Install App Prompt for PWA */}
        <InstallAppPrompt />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
